import { useState, useEffect } from 'react'
import {
  Title, Group, Card, Text, Stack,
  TextInput, Textarea, Button, NumberInput,
  Tabs, LoadingOverlay, ActionIcon, Paper, Checkbox,
} from '@mantine/core'
import {
  IconDeviceFloppy, IconRefresh, IconPlus, IconTrash,
  IconStethoscope, IconBuildingHospital,
  IconLayoutDashboard, IconStar, IconPackage,
  IconUsers, IconBolt, IconPhoto, IconArrowUp, IconArrowDown,
  IconEye, IconEyeOff, IconSettings, IconGripVertical,
} from '@tabler/icons-react'
import { useForm } from 'react-hook-form'
import toast from 'react-hot-toast'
import { api } from '@/lib/api'
import { useHomepage, useServices, useDoctors, useRooms, usePackages } from '@/lib/swr'
import type {
  UnifiedHomepage, HomeHeroContent, HomeHeroStat,
  HomeQuickService, HomeStatItem, HomeSectionConfig,
  Doctor, Room, Package,
} from '@/types'

// ─── Default empty state ─────────────────────────────────────────────────────

const EMPTY_HERO: HomeHeroContent = {
  title: '', subtitle: '', description: '', backgroundImage: '', stats: [], order: 0, isActive: true,
}

const DEFAULT_SECTIONS: HomeSectionConfig[] = [
  { key: 'hero', label: 'Hero', isVisible: true, order: 0 },
  { key: 'quickServices', label: 'Шуурхай үйлчилгээ', isVisible: true, order: 1 },
  { key: 'services', label: 'Үйлчилгээ', isVisible: true, order: 2 },
  { key: 'stats', label: 'Тоон үзүүлэлт', isVisible: true, order: 3 },
  { key: 'packages', label: 'Багцууд', isVisible: true, order: 4 },
  { key: 'doctors', label: 'Эмч нар', isVisible: true, order: 5 },
  { key: 'rooms', label: 'Өрөөнүүд', isVisible: true, order: 6 },
  { key: 'news', label: 'Мэдээ', isVisible: true, order: 7 },
]

const EMPTY_HOMEPAGE: UnifiedHomepage = {
  heroes: [],
  quickServices: [],
  stats: [],
  services: [],
  packages: [],
  doctors: [],
  rooms: [],
  news: [],
  sections: DEFAULT_SECTIONS,
}

// ─── Main Component ──────────────────────────────────────────────────────────

export default function HomepagePage() {
  const { data: serverData, isLoading: loading, mutate } = useHomepage()
  const [initialized, setInitialized] = useState(false)

  const { watch, setValue, handleSubmit, reset, formState: { isSubmitting } } =
    useForm<UnifiedHomepage>({ defaultValues: EMPTY_HOMEPAGE })

  const data = watch()
  const setData = (next: UnifiedHomepage) => {
    (Object.keys(next) as (keyof UnifiedHomepage)[]).forEach((k) => {
      setValue(k, next[k] as any, { shouldDirty: true })
    })
  }

  useEffect(() => {
    if (serverData && !initialized) {
      reset(serverData)
      setInitialized(true)
    }
  }, [serverData, initialized, reset])

  const load = () => { mutate() }

  const save = handleSubmit(async (current) => {
    try {
      const result = await api.admin.updateHomepage(current)
      if (result) reset(result)
      mutate(result || current, false)
      toast.success('Нүүр хуудасны мэдээлэл хадгалагдлаа')
    } catch {
      toast.error('Хадгалахад алдаа гарлаа')
    }
  })
  const saving = isSubmitting

  return (
    <Stack>
      <Group justify="space-between">
        <Title order={2}>Нүүр хуудас</Title>
        <Group>
          <Button leftSection={<IconRefresh size={16} />} variant="light" onClick={load} loading={loading}>
            Дахин ачаалах
          </Button>
          <Button leftSection={<IconDeviceFloppy size={16} />} onClick={save} loading={saving}>
            Хадгалах
          </Button>
        </Group>
      </Group>

      <Card withBorder pos="relative">
        <LoadingOverlay visible={loading} />
        <Tabs defaultValue="sections">
          <Tabs.List>
            <Tabs.Tab value="sections" leftSection={<IconSettings size={14} />}>Хэсгүүд</Tabs.Tab>
            <Tabs.Tab value="hero" leftSection={<IconLayoutDashboard size={14} />}>Hero</Tabs.Tab>
            <Tabs.Tab value="quickServices" leftSection={<IconBolt size={14} />}>Шуурхай</Tabs.Tab>
            <Tabs.Tab value="stats" leftSection={<IconStar size={14} />}>Тоон үзүүлэлт</Tabs.Tab>
            <Tabs.Tab value="services" leftSection={<IconStethoscope size={14} />}>Үйлчилгээ</Tabs.Tab>
            <Tabs.Tab value="packages" leftSection={<IconPackage size={14} />}>Багцууд</Tabs.Tab>
            <Tabs.Tab value="doctors" leftSection={<IconUsers size={14} />}>Эмч нар</Tabs.Tab>
            <Tabs.Tab value="rooms" leftSection={<IconBuildingHospital size={14} />}>Өрөөнүүд</Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="sections" pt="md">
            <SectionsEditor
              sections={data.sections?.length ? data.sections : DEFAULT_SECTIONS}
              onChange={(sections) => setData({ ...data, sections })}
            />
          </Tabs.Panel>
          <Tabs.Panel value="hero" pt="md">
            <HeroesEditor heroes={data.heroes ?? []} onChange={(heroes) => setData({ ...data, heroes })} />
          </Tabs.Panel>
          <Tabs.Panel value="quickServices" pt="md">
            <QuickServicesEditor items={data.quickServices} onChange={(quickServices) => setData({ ...data, quickServices })} />
          </Tabs.Panel>
          <Tabs.Panel value="stats" pt="md">
            <StatsEditor items={data.stats} onChange={(stats) => setData({ ...data, stats })} />
          </Tabs.Panel>
          <Tabs.Panel value="services" pt="md">
            <ServicesPickerEditor selectedIds={data.services ?? []} onChange={(services) => setData({ ...data, services })} />
          </Tabs.Panel>
          <Tabs.Panel value="packages" pt="md">
            <PackagesPickerEditor selectedIds={data.packages ?? []} onChange={(packages) => setData({ ...data, packages })} />
          </Tabs.Panel>
          <Tabs.Panel value="doctors" pt="md">
            <DoctorsPickerEditor selectedIds={data.doctors ?? []} onChange={(doctors) => setData({ ...data, doctors })} />
          </Tabs.Panel>
          <Tabs.Panel value="rooms" pt="md">
            <RoomsPickerEditor selectedIds={data.rooms ?? []} onChange={(rooms) => setData({ ...data, rooms })} />
          </Tabs.Panel>
        </Tabs>
      </Card>
    </Stack>
  )
}

// ─── Sections Visibility & Order Editor ─────────────────────────────────────

function SectionsEditor({ sections, onChange }: { sections: HomeSectionConfig[]; onChange: (s: HomeSectionConfig[]) => void }) {
  const sorted = [...sections].sort((a, b) => a.order - b.order)

  const moveUp = (i: number) => {
    if (i === 0) return
    const arr = [...sorted]
    ;[arr[i - 1], arr[i]] = [arr[i], arr[i - 1]]
    onChange(arr.map((s, idx) => ({ ...s, order: idx })))
  }
  const moveDown = (i: number) => {
    if (i >= sorted.length - 1) return
    const arr = [...sorted]
    ;[arr[i], arr[i + 1]] = [arr[i + 1], arr[i]]
    onChange(arr.map((s, idx) => ({ ...s, order: idx })))
  }
  const toggleVisible = (i: number) => {
    const arr = [...sorted]
    arr[i] = { ...arr[i], isVisible: !arr[i].isVisible }
    onChange(arr)
  }

  return (
    <Stack>
      <Text fw={500}>Нүүр хуудасны хэсгүүдийн дараалал, харагдах эсэх</Text>
      <Text size="sm" c="dimmed">Дээш/доош сумаар дарааллыг өөрчилж, нүдний товчоор харуулах/нуух боломжтой.</Text>

      {sorted.map((section, i) => (
        <Paper
          key={section.key}
          withBorder
          p="sm"
          style={{ opacity: section.isVisible ? 1 : 0.5 }}
        >
          <Group justify="space-between">
            <Group gap="sm">
              <IconGripVertical size={16} style={{ color: '#adb5bd' }} />
              <Text size="sm" fw={600}>{section.label}</Text>
              <Text size="xs" c="dimmed">({section.key})</Text>
              {!section.isVisible && <Text size="xs" c="red">(Нуугдсан)</Text>}
            </Group>
            <Group gap={4}>
              <ActionIcon variant="light" size="sm" onClick={() => moveUp(i)} disabled={i === 0}>
                <IconArrowUp size={14} />
              </ActionIcon>
              <ActionIcon variant="light" size="sm" onClick={() => moveDown(i)} disabled={i >= sorted.length - 1}>
                <IconArrowDown size={14} />
              </ActionIcon>
              <ActionIcon
                variant="light"
                size="sm"
                onClick={() => toggleVisible(i)}
                color={section.isVisible ? 'green' : 'gray'}
              >
                {section.isVisible ? <IconEye size={14} /> : <IconEyeOff size={14} />}
              </ActionIcon>
            </Group>
          </Group>
        </Paper>
      ))}
    </Stack>
  )
}

// ─── Heroes Carousel Editor ─────────────────────────────────────────────────

function HeroesEditor({ heroes, onChange }: { heroes: HomeHeroContent[]; onChange: (h: HomeHeroContent[]) => void }) {
  const add = () => onChange([...heroes, { ...EMPTY_HERO, order: heroes.length }])
  const remove = (i: number) => onChange(heroes.filter((_, idx) => idx !== i))
  const update = (i: number, updated: HomeHeroContent) => {
    const arr = [...heroes]
    arr[i] = updated
    onChange(arr)
  }
  const moveUp = (i: number) => {
    if (i === 0) return
    const arr = [...heroes]
    ;[arr[i - 1], arr[i]] = [arr[i], arr[i - 1]]
    onChange(arr)
  }
  const moveDown = (i: number) => {
    if (i >= heroes.length - 1) return
    const arr = [...heroes]
    ;[arr[i], arr[i + 1]] = [arr[i + 1], arr[i]]
    onChange(arr)
  }
  const toggleActive = (i: number) => {
    const arr = [...heroes]
    arr[i] = { ...arr[i], isActive: !arr[i].isActive }
    onChange(arr)
  }

  return (
    <Stack>
      <Group justify="space-between">
        <Text fw={500}>Hero слайдууд ({heroes.length})</Text>
        <Button size="xs" leftSection={<IconPlus size={14} />} variant="light" onClick={add}>Шинэ слайд нэмэх</Button>
      </Group>

      {heroes.length === 0 && (
        <Text c="dimmed" size="sm" ta="center" py="xl">
          Hero слайд байхгүй байна. &quot;Шинэ слайд нэмэх&quot; товчийг дарна уу.
        </Text>
      )}

      {heroes.map((hero, i) => (
        <Paper key={i} withBorder p="md" style={{ opacity: hero.isActive === false ? 0.6 : 1 }}>
          <Group justify="space-between" mb="sm">
            <Group gap="xs">
              <Text fw={600} size="sm">Слайд #{i + 1}</Text>
              {hero.isActive === false && <Text size="xs" c="red">(Идэвхгүй)</Text>}
            </Group>
            <Group gap={4}>
              <ActionIcon variant="light" size="sm" onClick={() => moveUp(i)} disabled={i === 0}>
                <IconArrowUp size={14} />
              </ActionIcon>
              <ActionIcon variant="light" size="sm" onClick={() => moveDown(i)} disabled={i >= heroes.length - 1}>
                <IconArrowDown size={14} />
              </ActionIcon>
              <ActionIcon variant="light" size="sm" onClick={() => toggleActive(i)} color={hero.isActive !== false ? 'green' : 'gray'}>
                {hero.isActive !== false ? <IconEye size={14} /> : <IconEyeOff size={14} />}
              </ActionIcon>
              <ActionIcon color="red" variant="light" size="sm" onClick={() => remove(i)}>
                <IconTrash size={14} />
              </ActionIcon>
            </Group>
          </Group>
          <HeroSlideEditor hero={hero} onChange={(h) => update(i, h)} />
        </Paper>
      ))}
    </Stack>
  )
}

function HeroSlideEditor({ hero, onChange }: { hero: HomeHeroContent; onChange: (h: HomeHeroContent) => void }) {
  const [uploading, setUploading] = useState(false)
  const update = (key: keyof HomeHeroContent, value: any) => onChange({ ...hero, [key]: value })

  const handleImageUpload = async (file: File | null) => {
    if (!file) return
    setUploading(true)
    try {
      const result = await api.admin.uploadImage(file)
      update('backgroundImage', result.url)
      toast.success('Зураг байршуулагдлаа')
    } catch {
      toast.error('Зураг байршуулахад алдаа гарлаа')
    } finally {
      setUploading(false)
    }
  }

  const addStat = () => update('stats', [...hero.stats, { value: '', label: '' }])
  const removeStat = (i: number) => update('stats', hero.stats.filter((_: any, idx: number) => idx !== i))
  const updateStat = (i: number, key: keyof HomeHeroStat, val: string) => {
    const stats = [...hero.stats]
    stats[i] = { ...stats[i], [key]: val }
    update('stats', stats)
  }

  return (
    <Stack gap="xs">
      <Group grow>
        <TextInput label="Гарчиг" value={hero.title} onChange={(e) => update('title', e.target.value)} />
        <TextInput label="Дэд гарчиг" value={hero.subtitle} onChange={(e) => update('subtitle', e.target.value)} />
      </Group>
      <Textarea label="Тайлбар" value={hero.description} onChange={(e) => update('description', e.target.value)} minRows={2} />

      {/* Image upload */}
      <div>
        <Text size="sm" fw={500} mb={4}>Дэвсгэр зураг</Text>
        <Group>
          <TextInput flex={1} placeholder="/image/xxx.webp" value={hero.backgroundImage} onChange={(e) => update('backgroundImage', e.target.value)} />
          <Button
            size="sm"
            variant="light"
            leftSection={<IconPhoto size={14} />}
            loading={uploading}
            onClick={() => {
              const input = document.createElement('input')
              input.type = 'file'
              input.accept = 'image/*'
              input.onchange = () => input.files?.[0] && handleImageUpload(input.files[0])
              input.click()
            }}
          >
            Зураг сонгох
          </Button>
        </Group>
        {hero.backgroundImage && (
          <img
            src={hero.backgroundImage}
            alt="Preview"
            style={{ marginTop: 8, maxHeight: 120, borderRadius: 8, objectFit: 'cover' }}
          />
        )}
      </div>

      <Group justify="space-between">
        <Text size="sm" fw={500}>Статистик</Text>
        <Button size="xs" leftSection={<IconPlus size={14} />} variant="subtle" onClick={addStat}>Нэмэх</Button>
      </Group>
      {hero.stats.map((stat, i) => (
        <Group key={i}>
          <TextInput flex={1} size="xs" placeholder="Утга (жнь: 15+)" value={stat.value} onChange={(e) => updateStat(i, 'value', e.target.value)} />
          <TextInput flex={1} size="xs" placeholder="Шошго" value={stat.label} onChange={(e) => updateStat(i, 'label', e.target.value)} />
          <ActionIcon color="red" variant="light" size="sm" onClick={() => removeStat(i)}><IconTrash size={14} /></ActionIcon>
        </Group>
      ))}
    </Stack>
  )
}

// ─── Quick Services Editor ───────────────────────────────────────────────────

function QuickServicesEditor({ items, onChange }: { items: HomeQuickService[]; onChange: (v: HomeQuickService[]) => void }) {
  const [uploadingIdx, setUploadingIdx] = useState<number | null>(null)
  const add = () => onChange([...items, { title: '', description: '', href: '', icon: '' }])
  const remove = (i: number) => onChange(items.filter((_, idx) => idx !== i))
  const update = (i: number, key: keyof HomeQuickService, val: string) => {
    const arr = [...items]
    arr[i] = { ...arr[i], [key]: val }
    onChange(arr)
  }

  const handleIconUpload = async (i: number, file: File | null) => {
    if (!file) return
    setUploadingIdx(i)
    try {
      const result = await api.admin.uploadImage(file)
      update(i, 'icon', result.url)
      toast.success('Icon байршуулагдлаа')
    } catch {
      toast.error('Icon байршуулахад алдаа гарлаа')
    } finally {
      setUploadingIdx(null)
    }
  }

  return (
    <Stack>
      <Group justify="space-between">
        <Text fw={500}>Шуурхай үйлчилгээ ({items.length})</Text>
        <Button size="xs" leftSection={<IconPlus size={14} />} variant="light" onClick={add}>Нэмэх</Button>
      </Group>
      {items.map((item, i) => (
        <Paper key={i} withBorder p="sm">
          <Group align="flex-start">
            <Stack flex={1} gap="xs">
              <Group grow>
                <TextInput label="Гарчиг" value={item.title} onChange={(e) => update(i, 'title', e.target.value)} />
                <TextInput label="Холбоос (href)" value={item.href} onChange={(e) => update(i, 'href', e.target.value)} />
              </Group>
              <TextInput label="Тайлбар" value={item.description} onChange={(e) => update(i, 'description', e.target.value)} />
              <div>
                <Text size="sm" fw={500} mb={4}>Icon зураг</Text>
                <Group>
                  <TextInput flex={1} placeholder="https://..." value={item.icon} onChange={(e) => update(i, 'icon', e.target.value)} />
                  <Button
                    size="sm"
                    variant="light"
                    leftSection={<IconPhoto size={14} />}
                    loading={uploadingIdx === i}
                    onClick={() => {
                      const input = document.createElement('input')
                      input.type = 'file'
                      input.accept = 'image/*'
                      input.onchange = () => input.files?.[0] && handleIconUpload(i, input.files[0])
                      input.click()
                    }}
                  >
                    Upload
                  </Button>
                </Group>
                {item.icon && (
                  <div style={{ marginTop: 8, width: 64, height: 64, borderRadius: 12, backgroundColor: '#006C67', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <img src={item.icon} alt="Icon preview" style={{ maxHeight: 32, maxWidth: 32, objectFit: 'contain' }} />
                  </div>
                )}
              </div>
            </Stack>
            <ActionIcon color="red" variant="light" mt={24} onClick={() => remove(i)}><IconTrash size={14} /></ActionIcon>
          </Group>
        </Paper>
      ))}
    </Stack>
  )
}

// ─── Stats Editor ────────────────────────────────────────────────────────────

function StatsEditor({ items, onChange }: { items: HomeStatItem[]; onChange: (v: HomeStatItem[]) => void }) {
  const add = () => onChange([...items, { value: 0, suffix: '', label: '' }])
  const remove = (i: number) => onChange(items.filter((_, idx) => idx !== i))
  const update = (i: number, key: keyof HomeStatItem, val: any) => {
    const arr = [...items]
    arr[i] = { ...arr[i], [key]: val }
    onChange(arr)
  }

  return (
    <Stack>
      <Group justify="space-between">
        <Text fw={500}>Тоон үзүүлэлт ({items.length})</Text>
        <Button size="xs" leftSection={<IconPlus size={14} />} variant="light" onClick={add}>Нэмэх</Button>
      </Group>
      {items.map((item, i) => (
        <Group key={i}>
          <NumberInput flex={1} label="Тоо" value={item.value} onChange={(v) => update(i, 'value', v || 0)} />
          <TextInput w={100} label="Дагавар" placeholder="+, %" value={item.suffix} onChange={(e) => update(i, 'suffix', e.target.value)} />
          <TextInput flex={1} label="Шошго" value={item.label} onChange={(e) => update(i, 'label', e.target.value)} />
          <ActionIcon color="red" variant="light" mt={24} onClick={() => remove(i)}><IconTrash size={14} /></ActionIcon>
        </Group>
      ))}
    </Stack>
  )
}

// ─── Services Picker Editor ─────────────────────────────────────────────────

function ServicesPickerEditor({ selectedIds, onChange }: { selectedIds: string[]; onChange: (v: string[]) => void }) {
  const { data: servicesData } = useServices({ limit: '100', status: 'active' })
  const allServices = servicesData?.data ?? []

  // Filter out IDs that no longer exist (soft-deleted services)
  const validIds = allServices.length > 0
    ? selectedIds.filter((id) => allServices.some((svc) => svc._id === id))
    : selectedIds

  const toggle = (id: string) => {
    if (validIds.includes(id)) {
      onChange(validIds.filter((sid) => sid !== id))
    } else {
      onChange([...validIds, id])
    }
  }

  const formatPrice = (price: number) => `₮${price.toLocaleString()}`

  return (
    <Stack>
      <Group justify="space-between">
        <Text fw={500}>Нүүр хуудсанд харуулах үйлчилгээ ({validIds.length} сонгосон)</Text>
      </Group>

      {allServices.length === 0 && (
        <Text c="dimmed" size="sm" ta="center" py="xl">
          Үйлчилгээ бүртгэгдээгүй байна. Эхлээд &quot;Үйлчилгээ&quot; хуудаснаас нэмнэ үү.
        </Text>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 8 }}>
        {allServices.map((svc) => {
          const isSelected = validIds.includes(svc._id)
          return (
            <Paper
              key={svc._id}
              withBorder
              p="sm"
              style={{
                cursor: 'pointer',
                borderColor: isSelected ? '#006C67' : undefined,
                backgroundColor: isSelected ? 'rgba(0,108,103,0.04)' : undefined,
              }}
              onClick={() => toggle(svc._id)}
            >
              <Group gap="sm" wrap="nowrap">
                <Checkbox checked={isSelected} onChange={() => toggle(svc._id)} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <Text size="sm" fw={600} truncate>{svc.name}</Text>
                  <Text size="xs" c="dimmed" truncate>{svc.description || svc.category}</Text>
                </div>
                <Text size="xs" fw={600} c="teal">{formatPrice(svc.price)}</Text>
              </Group>
            </Paper>
          )
        })}
      </div>
    </Stack>
  )
}

// ─── Packages Picker Editor ─────────────────────────────────────────────────

function PackagesPickerEditor({ selectedIds, onChange }: { selectedIds: string[]; onChange: (v: string[]) => void }) {
  const { data: packagesData } = usePackages({ limit: '100' })
  const allPackages: Package[] = packagesData?.data ?? []

  const validIds = allPackages.length > 0
    ? selectedIds.filter((id) => allPackages.some((p) => p._id === id))
    : selectedIds

  const toggle = (id: string) => {
    if (validIds.includes(id)) onChange(validIds.filter((s) => s !== id))
    else onChange([...validIds, id])
  }

  return (
    <Stack>
      <Text fw={500}>Нүүр хуудсанд харуулах багцууд ({validIds.length} сонгосон)</Text>

      {allPackages.length === 0 && (
        <Text c="dimmed" size="sm" ta="center" py="xl">
          Багц бүртгэгдээгүй байна. Эхлээд &quot;Багц эмчилгээ&quot; хуудаснаас нэмнэ үү.
        </Text>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 8 }}>
        {allPackages.map((p) => {
          const isSelected = validIds.includes(p._id)
          return (
            <Paper
              key={p._id}
              withBorder
              p="sm"
              style={{
                cursor: 'pointer',
                borderColor: isSelected ? '#006C67' : undefined,
                backgroundColor: isSelected ? 'rgba(0,108,103,0.04)' : undefined,
              }}
              onClick={() => toggle(p._id)}
            >
              <Group gap="sm" wrap="nowrap">
                <Checkbox checked={isSelected} onChange={() => toggle(p._id)} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <Text size="sm" fw={600} truncate>{p.name}</Text>
                  <Text size="xs" c="dimmed" truncate>{p.description || p.duration}</Text>
                </div>
                <Text size="xs" fw={600} c="teal">₮{p.price.toLocaleString()}</Text>
              </Group>
            </Paper>
          )
        })}
      </div>
    </Stack>
  )
}

// ─── Doctors Picker Editor ───────────────────────────────────────────────────

function DoctorsPickerEditor({ selectedIds, onChange }: { selectedIds: string[]; onChange: (v: string[]) => void }) {
  const { data: doctorsData } = useDoctors({ limit: '200' })
  const allDoctors: Doctor[] = doctorsData?.data ?? []

  const validIds = allDoctors.length > 0
    ? selectedIds.filter((id) => allDoctors.some((d) => d._id === id))
    : selectedIds

  const toggle = (id: string) => {
    if (validIds.includes(id)) {
      onChange(validIds.filter((sid) => sid !== id))
    } else {
      onChange([...validIds, id])
    }
  }

  return (
    <Stack>
      <Text fw={500}>Нүүр хуудсанд харуулах эмч нар ({validIds.length} сонгосон)</Text>

      {allDoctors.length === 0 && (
        <Text c="dimmed" size="sm" ta="center" py="xl">
          Эмч бүртгэгдээгүй байна. Эхлээд &quot;Эмч нар&quot; хуудаснаас нэмнэ үү.
        </Text>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 8 }}>
        {allDoctors.map((doc) => {
          const isSelected = validIds.includes(doc._id)
          return (
            <Paper
              key={doc._id}
              withBorder
              p="sm"
              style={{
                cursor: 'pointer',
                borderColor: isSelected ? '#006C67' : undefined,
                backgroundColor: isSelected ? 'rgba(0,108,103,0.04)' : undefined,
              }}
              onClick={() => toggle(doc._id)}
            >
              <Group gap="sm" wrap="nowrap">
                <Checkbox checked={isSelected} onChange={() => toggle(doc._id)} />
                {doc.image ? (
                  <img src={doc.image} alt="" style={{ width: 36, height: 36, borderRadius: '50%', objectFit: 'cover' }} />
                ) : (
                  <div style={{ width: 36, height: 36, borderRadius: '50%', background: '#006C67', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: 14, fontWeight: 700 }}>
                    {doc.name.charAt(0)}
                  </div>
                )}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <Text size="sm" fw={600} truncate>{doc.name}</Text>
                  <Text size="xs" c="dimmed" truncate>{doc.badge || doc.role}</Text>
                </div>
              </Group>
            </Paper>
          )
        })}
      </div>
    </Stack>
  )
}

// ─── Rooms Picker Editor ────────────────────────────────────────────────────

function RoomsPickerEditor({ selectedIds, onChange }: { selectedIds: string[]; onChange: (v: string[]) => void }) {
  const { data: roomsData } = useRooms({ limit: '100' })
  const allRooms: Room[] = roomsData?.data ?? []

  const validIds = allRooms.length > 0
    ? selectedIds.filter((id) => allRooms.some((r) => r._id === id))
    : selectedIds

  const toggle = (id: string) => {
    if (validIds.includes(id)) onChange(validIds.filter((s) => s !== id))
    else onChange([...validIds, id])
  }

  return (
    <Stack>
      <Text fw={500}>Нүүр хуудсанд харуулах өрөөнүүд ({validIds.length} сонгосон)</Text>

      {allRooms.length === 0 && (
        <Text c="dimmed" size="sm" ta="center" py="xl">
          Өрөө бүртгэгдээгүй байна. Эхлээд &quot;Өрөөнүүд&quot; хуудаснаас нэмнэ үү.
        </Text>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 8 }}>
        {allRooms.map((r) => {
          const isSelected = validIds.includes(r._id)
          return (
            <Paper
              key={r._id}
              withBorder
              p="sm"
              style={{
                cursor: 'pointer',
                borderColor: isSelected ? '#006C67' : undefined,
                backgroundColor: isSelected ? 'rgba(0,108,103,0.04)' : undefined,
              }}
              onClick={() => toggle(r._id)}
            >
              <Group gap="sm" wrap="nowrap">
                <Checkbox checked={isSelected} onChange={() => toggle(r._id)} />
                {r.image && <img src={r.image} alt="" style={{ width: 48, height: 36, borderRadius: 4, objectFit: 'cover' }} />}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <Text size="sm" fw={600} truncate>{r.name}</Text>
                  <Text size="xs" c="dimmed" truncate>{r.type} · {r.capacity} хүн</Text>
                </div>
                <Text size="xs" fw={600} c="teal">₮{r.price.toLocaleString()}</Text>
              </Group>
            </Paper>
          )
        })}
      </div>
    </Stack>
  )
}

